import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Icon from '../Icon/Icon';

const isImageSource = (source) => {
  if (typeof source !== 'string') {
    return false;
  }
  const trimmed = source.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.startsWith('data:image') || trimmed.startsWith('blob:')) {
    return true;
  }
  const plainPath = trimmed.split('?')[0] || '';
  return /\.(png|jpe?g|gif|webp|avif|bmp|svg)$/i.test(plainPath);
};

const formatDuration = (seconds) => {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) {
    return null;
  }
  const totalSeconds = Math.round(seconds);
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.max(0, totalSeconds - mins * 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const PreviewImage = ({
  onClick,
  image,
  thumbnail,
  mediaUrl,
  mediaType = 'image',
  metadata,
  likes,
  comments,
  filter,
  commentCount,
  isLocked = false,
  lockMessage,
}) => {
  const isVideo = mediaType === 'video';
  const previewSrc = mediaUrl || image || thumbnail;
  const playableSrc = mediaUrl || image || thumbnail;
  const [generatedPoster, setGeneratedPoster] = useState(null);
  const videoRef = useRef(null);
  const hasProvidedPoster =
    (thumbnail && isImageSource(thumbnail)) ||
    (image && isImageSource(image));
  const shouldGeneratePoster = isVideo && !hasProvidedPoster;
  const effectivePoster =
    (hasProvidedPoster && (thumbnail || image)) ||
    generatedPoster ||
    undefined;

  useEffect(() => {
    setGeneratedPoster(null);
  }, [shouldGeneratePoster, mediaUrl]);

  const handleVideoPreview = useCallback(() => {
    if (!shouldGeneratePoster || generatedPoster) {
      return;
    }
    const videoElement = videoRef.current;
    if (
      !videoElement ||
      videoElement.readyState < 2 ||
      !videoElement.videoWidth ||
      !videoElement.videoHeight
    ) {
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      context.drawImage(
        videoElement,
        0,
        0,
        videoElement.videoWidth,
        videoElement.videoHeight
      );
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      setGeneratedPoster(dataUrl);
    } catch (err) {
      // Ignore failures (cross-origin, etc.) and fallback to default behavior.
    }
  }, [generatedPoster, shouldGeneratePoster]);

  const durationLabel =
    metadata && typeof metadata === 'object'
      ? formatDuration(metadata.durationSeconds)
      : null;

  const totalComments = typeof commentCount === 'number'
    ? commentCount
    : Array.isArray(comments)
    ? comments.length
    : Number(comments) || 0;

  const totalLikes = typeof likes === 'number' ? likes : Number(likes) || 0;

  const videoPreload = shouldGeneratePoster || !effectivePoster ? 'auto' : 'metadata';

  return (
    <figure onClick={onClick} className="preview-image">
      {isLocked ? (
        <div className="preview-image__locked">
          <div className="preview-image__locked-content">
            <Icon icon="lock-closed" className="icon--white" />
            <span className="preview-image__locked-text">
              {lockMessage || "Subscribe to unlock"}
            </span>
          </div>
        </div>
      ) : isVideo ? (
        <video
          ref={videoRef}
          src={playableSrc}
          poster={effectivePoster}
          playsInline
          muted
          loop
          preload={videoPreload}
          crossOrigin={shouldGeneratePoster ? 'anonymous' : undefined}
          onLoadedData={shouldGeneratePoster ? handleVideoPreview : undefined}
        />
      ) : (
        <img src={previewSrc} alt="User post" style={{ filter }} />
      )}
      {isVideo && !isLocked && (
        <span className="preview-image__badge">
          <Icon icon="play" className="icon--white" />
          {durationLabel && <span>{durationLabel}</span>}
        </span>
      )}
      {!isLocked && (
        <div className="preview-image__overlay">
          <span className="preview-image__content">
            <div className="preview-image__icon">
              <Icon icon="heart" className="icon--white" />
              <span>{totalLikes}</span>
            </div>
            <div className="preview-image__icon">
              <Icon icon="chatbubbles" className="icon--white" />
              <span>{totalComments}</span>
            </div>
          </span>
        </div>
      )}
    </figure>
  );
};

PreviewImage.propTypes = {
  onClick: PropTypes.func,
  image: PropTypes.string,
  thumbnail: PropTypes.string,
  mediaUrl: PropTypes.string,
  mediaType: PropTypes.string,
  metadata: PropTypes.object,
  likes: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  comments: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
  commentCount: PropTypes.number,
  isLocked: PropTypes.bool,
  lockMessage: PropTypes.string,
};

export default PreviewImage;
